import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchProjects, fetchHomepageGallery, fetchSiteSettings, imgUrl, cardImage } from "../api";
import HeroGallery from "./HeroGallery";
import HeroLoader, { minLoaderDelay, maxLoaderDelay, preloadImages } from "./HeroLoader";
import CreditsBlock from "./CreditsBlock";
import AboutText from "./AboutText";
import TypewriterText from "./TypewriterText";
import VideoPlayer from "./VideoPlayer";
import "./MobileHomePage.css";

// Cached images can already be complete before React attaches onLoad
const markLoaded = (el) => {
  if (el && el.complete) el.classList.add("loaded");
};

export default function MobileHomePage() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [aboutText, setAboutText] = useState("");
  const [loadProgress, setLoadProgress] = useState(0);
  const [loaderDone, setLoaderDone] = useState(false);
  const pendingGalleryRef = useRef([]);

  const [copied, setCopied] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  const ALL_TAGS = ["Video", "Image", "Graphic Design", "AI"];

  const [transitionVisible, setTransitionVisible] = useState(false);
  const [transitionFading, setTransitionFading] = useState(false);
  const transitionTimeoutRef = useRef(null);
  const transitionHideRef = useRef(null);

  const projectContentRef = useRef(null);
  const scrollableRef = useRef(null);
  const infiniteScrollRef = useRef();
  const CYCLE_COUNT = 7;

  const aboutSectionRef = useRef(null);
  const workSectionRef = useRef(null);
  const pendingScrollRef = useRef(null);

  const visibleProjects = projects.filter((proj) => proj.show);
  // The filter only narrows the homepage grid; the project-page index
  // always lists every project.
  const filteredProjects = visibleProjects.filter(
    (proj) => !activeFilter || proj.tags?.includes(activeFilter)
  );

  // Reveal gallery items as they scroll into view — no React state, no loops
  useEffect(() => {
    if (!selectedProject) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: "400px" }
    );

    const rafId = requestAnimationFrame(() => {
      document
        .querySelectorAll(".mobile-image-wrapper")
        .forEach((el) => observer.observe(el));
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [selectedProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = infiniteScrollRef.current;
    if (!el || visibleProjects.length === 0) return;

    const singleCycleWidth = el.scrollWidth / CYCLE_COUNT;
    el.scrollLeft = (CYCLE_COUNT / 2) * singleCycleWidth;

    const handleScroll = () => {
      const maxScrollLeft = el.scrollWidth;
      const scrollLeft = el.scrollLeft;
      const resetTo = (CYCLE_COUNT / 2) * singleCycleWidth;
      if (scrollLeft < singleCycleWidth * 2) {
        el.scrollLeft = resetTo;
      } else if (scrollLeft > maxScrollLeft - singleCycleWidth * 2) {
        el.scrollLeft = resetTo;
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [visibleProjects.length]);

  const triggerTransition = useCallback(() => {
    setTransitionVisible(true);
    setTransitionFading(false);
    clearTimeout(transitionTimeoutRef.current);
    clearTimeout(transitionHideRef.current);
    transitionTimeoutRef.current = setTimeout(() => {
      setTransitionFading(true);
      transitionHideRef.current = setTimeout(() => setTransitionVisible(false), 700);
    }, 200);
  }, []);

  const scrollToTop = useCallback(() => {
    if (scrollableRef.current) scrollableRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    const minDelay = minLoaderDelay();
    const maxDelay = maxLoaderDelay();

    async function load() {
      const [projectData, galleryData, settings] = await Promise.all([
        fetchProjects(),
        fetchHomepageGallery(),
        fetchSiteSettings(),
      ]);
      setProjects(projectData);
      setLoadProgress(0.2);

      // Hold the loader until the first gallery slides and the top of the
      // project grid have downloaded (same URLs the page renders, so they come
      // straight from cache), but never longer than MAX_LOADER_MS.
      const firstImages = [
        ...galleryData.slice(0, 2).map((item) => item.url && imgUrl(item.url, 2000)),
        ...projectData
          .filter((proj) => proj.show)
          .slice(0, 8)
          .map((proj) => imgUrl(cardImage(proj).url, 700)),
      ];
      await Promise.race([
        Promise.all([
          preloadImages(firstImages, (p) => setLoadProgress(0.2 + 0.8 * p)),
          minDelay,
        ]),
        maxDelay,
      ]);
      setAboutText(settings.aboutText);
      // The gallery swaps in once the bar has visibly reached 100%
      pendingGalleryRef.current = galleryData;
      setLoaderDone(true);
    }
    load();
  }, []);

  useEffect(() => {
    if (!projects.length) return;
    if (slug) {
      const project = projects.find((p) => p.slug === slug);
      setSelectedProject(project || null);
      scrollToTop();
      return;
    }
    setSelectedProject(null);
    scrollToTop();
  }, [projects, slug, scrollToTop]);


  useEffect(() => {
    if (!selectedProject && pendingScrollRef.current) {
      const target = pendingScrollRef.current;
      pendingScrollRef.current = null;
      setTimeout(() => {
        if (target === "about") aboutSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        if (target === "work") workSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    }
  }, [selectedProject]);

  const handleProjectClick = useCallback(
    (proj) => {
      const target = typeof proj === "string"
        ? projects.find((p) => p.slug === proj)
        : proj;
      if (!target) return;
      triggerTransition();
      setTimeout(() => {
        setSelectedProject(target);
        scrollToTop();
        navigate(`/project/${target.slug}`);
      }, 24);
    },
    [projects, triggerTransition, scrollToTop, navigate]
  );

  const handleNameClick = useCallback(() => {
    if (selectedProject) {
      triggerTransition();
      setTimeout(() => {
        setSelectedProject(null);
        scrollToTop();
        navigate("/");
      }, 24);
    } else {
      scrollToTop();
    }
  }, [selectedProject, triggerTransition, scrollToTop, navigate]);

  const handleWorkClick = useCallback(() => {
    if (selectedProject) {
      triggerTransition();
      setTimeout(() => {
        setSelectedProject(null);
        scrollToTop();
        navigate("/");
      }, 24);
    } else {
      workSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedProject, triggerTransition, scrollToTop, navigate]);

  const handleAboutClick = useCallback(() => {
    if (selectedProject) {
      pendingScrollRef.current = "about";
      triggerTransition();
      setTimeout(() => {
        setSelectedProject(null);
        scrollToTop();
        navigate("/");
      }, 24);
    } else {
      aboutSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedProject, triggerTransition, scrollToTop, navigate]);

  const handleVideoMeta = (e) => {
    const v = e.currentTarget;
    if (v.videoHeight > v.videoWidth) {
      v.style.maxHeight = '100vh';
      v.style.width = 'auto';
      v.style.maxWidth = '100%';
    }
  };

  const renderVideo = (url, key) => {
    if (!url) return null;
    const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
    if (isYouTube) {
      const videoId = url.includes("youtu.be/")
        ? url.split("youtu.be/")[1].split("?")[0]
        : new URL(url).searchParams.get("v");
      return (
        <div className="project-video-wrapper" key={`yt-${key}`}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    return (
      <div className="project-video-wrapper" key={`vid-${key}`}>
        <video src={url} className="project-video" controls playsInline muted preload="metadata" />
      </div>
    );
  };

  const renderGalleryItem = (item, index) => {
    if ((item._type === "file" || item._type === "galleryVideo") && item.videoUrl) {
      const showControls = !!selectedProject?.galleryVideoControls;
      if (showControls) {
        return <VideoPlayer src={item.videoUrl} className="mobile-image" playsInline autoPlay initialMuted />;
      }
      return (
        <video
          src={item.videoUrl}
          className="mobile-image"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          webkit-playsinline="true"
          onLoadedMetadata={handleVideoMeta}
        />
      );
    }
    return (
      <img
        src={imgUrl(item.url, 1200)}
        alt=""
        className="mobile-image"
        width={item.width || undefined}
        height={item.height || undefined}
        fetchPriority={index === 0 ? "high" : undefined}
        decoding="async"
        ref={markLoaded}
        onLoad={(e) => e.currentTarget.classList.add("loaded")}
      />
    );
  };

  return (
    <div className="mobile-homepage">
      <div className="mobile-nav">
        {selectedProject && (
          <div className="mobile-nav-back" onClick={handleNameClick} aria-label="Back to home">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </div>
        )}
        <div className="mobile-nav-links">
          <div onClick={handleWorkClick}>Projects</div>
          <div onClick={handleAboutClick}>About</div>
          <div
            className={`mobile-contact-btn${copied ? " contact-copied" : ""}`}
            onClick={() => {
              navigator.clipboard.writeText("dubois.leo.design@gmail.com");
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            <span className="contact-label">Contact</span>
            <span className="contact-label-copied">Copied</span>
          </div>
        </div>
      </div>

      <div className="mobile-scrollable" ref={scrollableRef}>
        {selectedProject ? (
          <div className="mobile-project-content" key={selectedProject._id} ref={projectContentRef}>
            <h2 className="mobile-project-title"><TypewriterText text={selectedProject.title} charDelay={28} startDelay={0} /></h2>
            <p className="mobile-project-description"><TypewriterText text={selectedProject.description} charDelay={8} startDelay={0} /></p>
            <CreditsBlock key={selectedProject._id} credits={selectedProject.credits} />

            {selectedProject.video1?.url && (
              <div className="mobile-image-wrapper">
                {renderVideo(selectedProject.video1.url, 1)}
              </div>
            )}
            {selectedProject.video2?.url && (
              <div className="mobile-image-wrapper">
                {renderVideo(selectedProject.video2.url, 2)}
              </div>
            )}
            {selectedProject.gallery.map((item, i) => (
              <div key={item.id} className="mobile-image-wrapper">
                {renderGalleryItem(item, i)}
              </div>
            ))}

            <div className="mobile-thumbnail-index-wrapper">
              <div className="mobile-thumbnail-index-bar" ref={infiniteScrollRef}>
                {[...Array(CYCLE_COUNT)].map((_, i) => (
                  <React.Fragment key={i}>
                    {visibleProjects.map((proj) => {
                      const img = cardImage(proj);
                      return (
                        <img
                          key={`${proj._id}-${i}`}
                          src={imgUrl(img.url || "", 240)}
                          alt={proj.title}
                          width={img.width || undefined}
                          height={img.height || undefined}
                          decoding="async"
                          ref={markLoaded}
                          onLoad={(e) => e.currentTarget.classList.add("loaded")}
                          className={`mobile-thumbnail-index-image ${
                            selectedProject._id === proj._id ? "active" : ""
                          }`}
                          onClick={() => handleProjectClick(proj)}
                        />
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <section className="mobile-gallery-section">
              {galleryItems.length > 0 ? (
                <HeroGallery
                  items={galleryItems}
                  onProjectClick={(slug) => handleProjectClick(slug)}
                />
              ) : (
                <HeroLoader
                  progress={loadProgress}
                  done={loaderDone}
                  onComplete={() => setGalleryItems(pendingGalleryRef.current)}
                />
              )}
            </section>

            <section className="mobile-about-section" ref={aboutSectionRef}>
              <AboutText className="mobile-about-text" text={aboutText} />
            </section>

            <section className="mobile-work-section" ref={workSectionRef}>
              <div className="mobile-filter-bar">
                <span className="mobile-filter-label">Filter</span>
                {ALL_TAGS.map((tag) => (
                  <div
                    key={tag}
                    className={`mobile-filter-tag${activeFilter === tag ? " active" : ""}`}
                    onClick={() => setActiveFilter(activeFilter === tag ? null : tag)}
                  >
                    {tag}
                  </div>
                ))}
              </div>
              <div className="mobile-project-grid">
                {filteredProjects.map((proj) => {
                  const img = cardImage(proj);
                  return (
                    <div
                      key={proj._id}
                      className="mobile-project-card"
                      onClick={() => handleProjectClick(proj)}
                    >
                      <img
                        src={imgUrl(img.url || "", 700)}
                        alt={proj.title}
                        className="mobile-thumbnail"
                        width={img.width || undefined}
                        height={img.height || undefined}
                        loading="lazy"
                        decoding="async"
                        ref={markLoaded}
                        onLoad={(e) => e.currentTarget.classList.add("loaded")}
                      />
                      <div className="mobile-project-title">{proj.title}</div>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>

      {transitionVisible && (
        <div className={`mobile-transition-overlay${transitionFading ? " fading" : ""}`} />
      )}
    </div>
  );
}
